// services/kontoConfig.js — кодовете за Конто 6: файлът по подразбиране,
// слят с презаписите на фирмата (ПРОМТ-7).
//
// Защо: константите живееха на ДВЕ места (K в src/invoices/xlsx.js и
// DOC_CODES в services/kontoExport.js) и бяха еднакви за всички фирми, а в
// Конто на всеки клиент кодовете на документи, контрагенти и сделки са
// различни. Сега има ЕДИН източник — config/konto.config.json — и всяка фирма
// презаписва каквото ѝ трябва в company.konto (routes/companies.js).
// Празно поле в презаписа = „вземи от файла".
//
// Промени 16.10.2026 (т. 3г, 3д, 3ж): презаписва се ВСЯКА стойност, която
// файлът пише сам — валута, курс, кодовете на плащането, редът „РЕАЛИЗАЦИЯ",
// мярката, вальорът, допълнителните реквизити, отделен код на документа при
// плащане в брой и как се праща кодът на контрагента. Екранът е „Фактури →
// Действия → Настройки за Конто" (src/shared/kontoSettings.js).
//
// CJS нарочно (както services/siddo.js): require-ва се от сървъра и тестовете,
// а Vite го внася в браузъра без промяна.

const DEFAULTS = require('../config/konto.config.json');
// Готовите настройки за записванията по ЕИК (от отговора на агента на Конто)
// — бутонът „Попълни по данните от Конто" в „Настройки за Конто".
const RECORD_PRESETS = require('../config/konto.records.presets.json');
// Как са разбити сметките (нивата на партидата) във всяка фирма в Конто — от
// ОТГОВОР-ПРОМТ-24 (27.10.2026), по ЕИК. От тях идват нивата на 412 и 4532,
// когато фирмата не ги е подредила сама в „Настройки за Конто".
const ANALYTICS = require('../config/konto.analytics.json');
const ANALYTICS_BY_KEY = new Map(Object.keys(ANALYTICS).filter((k) => k !== '_meta').map((k) => [k.replace(/^0+/, ''), ANALYTICS[k]]));
// Записът на фирмата в данните от Конто (ЕИК без водещите нули — „040…" = „40…").
// ПРОМТ-31: първо данните от базата (konto_account_levels, закачени от
// services/kontoAnalytics.withAnalytics като company.konto_analytics —
// { accounts: { '601': { levels: [{ no, kind, nom, nom_name, len, role }],
// roles, text } } }); без тях — JSON файлът (само 401…4532). Формата е една:
// { '<сметка>': { levels: [роли] | null, text, detail: [нивата] }, name }.
function analyticsOf(company) {
  const own = company && company.konto_analytics && company.konto_analytics.accounts;
  const k = String((company && company.eik) || '').replace(/\D/g, '').replace(/^0+/, '');
  const fromJson = (k && ANALYTICS_BY_KEY.get(k)) || null;
  if (own && typeof own === 'object') {
    // Сметка, която я няма в базата (качен е само файлът за гр. 30/60), идва
    // от JSON-а — иначе проверката на нивата на 401/4531 би изчезнала тихо.
    const out = Object.assign({}, fromJson || {});
    for (const [acc, a] of Object.entries(own)) {
      if (!a || typeof a !== 'object') continue;
      out[acc] = { levels: Array.isArray(a.roles) ? a.roles.slice() : null, text: a.text || '', detail: Array.isArray(a.levels) ? a.levels : [], used: a.used || null, name: a.name || '' };
    }
    out.name = company.name || '';
    return out;
  }
  return fromJson;
}

// Секциите, които фирмата може да презаписва ключ по ключ.
// cogs (ПРОМТ-11): папка, вид документ, партиди и текст на файла с
// изписванията (services/kontoCogs.js: DEFAULTS) — подразбирането е там, не
// във файла, защото е за „Импорт на записвания", не за „Импорт на документи".
// doc_codes_cash: кодът на документа, когато е платен в брой (празно = като
// по банков път); pay_codes: кодовете „в брой"/„по банков път"; values:
// единичните стойности (VALUE_KEYS); fields: допълнителните реквизити.
const SECTIONS = ['doc_codes', 'doc_codes_cash', 'partner_types', 'deal_types', 'measure_codes', 'cogs', 'pay_codes', 'values', 'fields', 'records'];

// records (Етап 3, по ОТГОВОР-ПРОМТ-20): продажбите през „Импорт на
// записвания" — CRM-ът сам казва сметките (services/kontoRecords.js).
// mode: documents = Excel, както досега (подразбирането — фирмата получава
// същия файл); records = TXT. Нивата на партидата на 412 и 4532 са различни по
// фирма (Маятико срещу останалите) — затова са избор, не свободен текст.
const RECORD_MODES = ['documents', 'records'];
// Отделните настройки на ДОСТАВКИТЕ (Макс, 27.10.2026: „настройките при
// фактурите не трябва да са същите като при доставките"). company.konto.
// purchases = { pay_codes, deal_types, values, fields } — лягат върху общите
// само за файла с доставките; празно = като при продажбите (така фирма, която
// не е пипната, получава същия файл като досега).
// records (ПРОМТ-29, 27.10.2026): доставките са винаги записвания (TXT) —
// папка, текст, сметките 401/4531, разходната по подразбиране, нивата на
// 401 и 4531 и видовете сделка без право / с частичен кредит
// (services/kontoPurchaseRecords.js). Подразбирането е purchase_records във файла.
// ПРОМТ-31 (28.10.2026): measures — мярка (ключ measureKey) → код в ном. $3;
// items — категория на разхода → код на статията на фирмата ('new' = CRM-ът
// дава следващия свободен код при експорта); records.activity_code — кодът в
// ном. 20 („Вид дейност"); records.items_confirmed — кога Макс е потвърдил
// картата (без това експортът на доставки спира).
const PURCHASE_SECTIONS = ['pay_codes', 'deal_types', 'values', 'fields', 'records', 'measures', 'items'];
// default_category (30.10.2026): категорията на ред без артикул, когато OCR-ът не познае друга
const PURCHASE_RECORD_KEYS = ['folder', 'text', 'acc_supplier', 'acc_vat', 'acc_expense', 'lot_401', 'lot_4531', 'deal_full', 'deal_no_credit', 'deal_partial', 'deal_no_vat', 'activity_code', 'items_confirmed', 'default_category', 'advance_mode', 'project_code', 'mol_code'];
// Авансите към доставчици (ПРОМТ-44, Макс 30.10.2026: „нека има избор"):
// 402 — платеният аванс по 402, закрива се при окончателната (подразбирането);
// 401 — без 402, авансът стои по 401 на доставчика с партидата на авансовата;
// none — CRM-ът не ги изнася, водят се на ръка в Конто.
const ADVANCE_MODES = ['402', '401', 'none'];
// ОТГОВОР-ПРОМТ-32 (29.10.2026): покупките по фирма — папка, вид документ
// (фактура/КИ/ДИ) и вид сделка (ном. 51) от ръчните статии в Конто за 12
// месеца. Лягат, когато фирмата не ги е задала сама.
const PURCHASES = require('../config/konto.purchases.json');
const PURCHASES_BY_KEY = new Map(Object.keys(PURCHASES).filter((k) => k !== '_meta').map((k) => [k.replace(/^0+/, ''), PURCHASES[k]]));
function purchasesOf(company) {
  const k = String((company && company.eik) || '').replace(/\D/g, '').replace(/^0+/, '');
  return (k && PURCHASES_BY_KEY.get(k)) || null;
}
const LOT_412 = ['doc_date_partner', 'partner_doc_date'];
const LOT_4532 = ['deal_partner_doc', 'deal_partner_doc_date'];
// Нивата се нареждат свободно по фирма (Макс, 27.10.2026: „412 трябва да
// може да се настройва — при всяка фирма е различно"): записват се като
// „partner,no,date" — до толкова нива, колкото части има, без повторения.
// Двете стари стойности остават (заварените фирми).
const LOT_PARTS = { lot_412: ['partner', 'no', 'date'], lot_4532: ['deal', 'partner', 'no', 'date'], lot_401: ['partner', 'no', 'date'], lot_4531: ['deal', 'partner', 'no', 'date'] };
const LOT_PRESETS = {
  doc_date_partner: ['no', 'date', 'partner'], partner_doc_date: ['partner', 'no', 'date'],
  deal_partner_doc: ['deal', 'partner', 'no'], deal_partner_doc_date: ['deal', 'partner', 'no', 'date'],
};
// Нивата като списък; невалидното — null.
function lotLevels(key, v) {
  const s = String(v || '').trim();
  // Сметка без аналитичност в Конто (СД ХЕВИС, 4532) — партида без нива.
  if (s === 'none') return [];
  if (LOT_PRESETS[s] && (key === 'lot_412' ? LOT_412 : key === 'lot_4532' ? LOT_4532 : []).includes(s)) return LOT_PRESETS[s].slice();
  const parts = s.split(',').map((x) => x.trim()).filter(Boolean);
  const ok = LOT_PARTS[key];
  if (!ok) return null;
  if (!parts.length || parts.length > ok.length || new Set(parts).size !== parts.length || parts.some((x) => !ok.includes(x))) return null;
  return parts;
}
const REVENUE_SLOTS = 6;
const RECORD_KEYS = ['mode', 'folder', 'acc_cash', 'acc_customer', 'acc_advance', 'acc_vat', 'lot_412', 'lot_4532', 'deal_20', 'deal_9', 'text'];
for (let i = 1; i <= REVENUE_SLOTS; i++) RECORD_KEYS.push('rev' + i + '_name', 'rev' + i + '_account', 'rev' + i + '_lot');

// Единичните стойности, които секцията values презаписва на горното ниво —
// там ги чете builder-ът (K.currency, K.article_name…).
// purchase_cc_field (27.10.2026): в коя допълнителна колона на файла с
// доставките отива № на счетоводната статия (СС) — „Импорт на документи" няма
// своя колона за него. Празно = не се праща.
const VALUE_KEYS = ['currency', 'rate', 'article_type', 'article_id', 'article_name', 'measure', 'valeur', 'partner_code', 'purchase_cc_field'];
const CC_FIELD_RE = /^H\.FIELD_(?:[1-9]|1\d|20)$/;
// Вальорът: последният ден на месеца (както досега) или датата на документа.
const VALEUR = ['eom', 'doc'];
// Кодът на контрагента във файла: шифърът, а без шифър — ЕИК-ът (както
// досега) или САМО шифърът (без шифър файлът спира — services/kontoValidate).
const PARTNER_CODE = ['acc_or_eik', 'acc_only'];
// Допълнителните реквизити на файла: 20 в заглавието и 20 на реда.
const FIELD_KEYS = [];
for (let i = 1; i <= 20; i++) FIELD_KEYS.push('H.FIELD_' + i);
for (let i = 1; i <= 20; i++) FIELD_KEYS.push('D.FIELD_' + i);

// Позволените ключове и дължината на стойността по секция (за записа).
// keys: null = свободни ключове (мерките са свободен текст, cogs има свои).
const LIMITS = {
  doc_codes: { keys: ['invoice', 'credit', 'debit', 'purchase_invoice', 'purchase_credit', 'purchase_debit'], max: 20 },
  doc_codes_cash: { keys: ['invoice', 'credit', 'debit'], max: 20 },
  partner_types: { keys: ['customer', 'supplier'], max: 20 },
  deal_types: { keys: ['domestic', 'eu_b2b', 'eu_b2c_oss', 'eu_b2c_domestic', 'reverse_charge', 'exempt'], max: 20 },
  measure_codes: { keys: null, max: 20 },
  cogs: { keys: null, max: 60 },
  pay_codes: { keys: ['cash', 'bank'], max: 20 },
  values: { keys: VALUE_KEYS, max: 60 },
  fields: { keys: FIELD_KEYS, max: 100 },
  records: { keys: RECORD_KEYS, max: 60 },
};

function isEmpty(v) {
  return v === undefined || v === null || String(v).trim() === '';
}

function clip(v, max) {
  return String(v == null ? '' : v).replace(/[\r\n\t]+/g, ' ').trim().slice(0, max);
}

// Курсът: число над нула (приема и запетая). Иначе — подразбирането.
function toRate(v, def) {
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : def;
}

function checkValues(r) {
  if (r.valeur && !VALEUR.includes(r.valeur)) throw new Error('Невалиден вальор: „' + r.valeur + '".');
  if (r.partner_code && !PARTNER_CODE.includes(r.partner_code)) throw new Error('Невалиден избор за кода на контрагента: „' + r.partner_code + '".');
  if (r.purchase_cc_field && !CC_FIELD_RE.test(r.purchase_cc_field)) throw new Error('Невалидна колона за № на статия: „' + r.purchase_cc_field + '".');
  if (r.rate && toRate(r.rate, null) == null) throw new Error('Валутният курс трябва да е число над нула.');
}

// Чистене на презаписите за запис. Връща САМО секциите, които ги има в
// заявката — секция, която липсва, не се пипа (routes/companies.js слива).
// Невалиден избор (вальор, код на контрагента, курс) хвърля с текста на
// отказа — по-добре отказ, отколкото тихо „по подразбиране".
function cleanSection(s, raw, limits) {
  const sec = (raw && typeof raw === 'object') ? raw : {};
  const lim = limits || LIMITS[s];
  const r = {};
  for (const key of Object.keys(sec)) {
    const k = clip(key, 40);
    if (!k || (lim.keys && !lim.keys.includes(k))) continue;
    r[k] = clip(sec[key], lim.max);
  }
  return r;
}
function sanitize(input) {
  const o = (input && typeof input === 'object') ? input : {};
  const out = {};
  if ('purchases' in o) {
    const p = (o.purchases && typeof o.purchases === 'object') ? o.purchases : {};
    out.purchases = {};
    for (const s of PURCHASE_SECTIONS) {
      if (!(s in p)) continue;
      out.purchases[s] = cleanSection(s, p[s], s === 'records' ? { keys: PURCHASE_RECORD_KEYS, max: 60 } : (s === 'measures' || s === 'items') ? { keys: null, max: 20 } : null);
      if (s === 'values') checkValues(out.purchases[s]);
      if (s === 'records') checkPurchaseRecords(out.purchases[s]);
    }
    // ПРОМТ-45: изключенията от общата памет по контрагент (ЕИК → категория),
    // само за тази фирма — services/counterpartyMemory.js.
    if ('memory_override' in p) out.purchases.memory_override = require('./counterpartyMemory').sanitizeOverrides(p.memory_override);
  }
  for (const s of SECTIONS) {
    if (!(s in o)) continue;
    const r = cleanSection(s, o[s]);
    if (s === 'values') {
      checkValues(r);
    }
    if (s === 'records') checkRecords(r);
    if (s === 'cogs') checkCogs(r);
    out[s] = r;
  }
  return out;
}

// Сметка: само цифри (в Конто сметката е число — 411, 4532). Партида: нивата
// през „/", всяко до 20 знака (толкова е едно ниво във файла).
const ACCOUNT_RE = /^\d{1,10}$/;
function checkRecords(r) {
  if (r.mode && !RECORD_MODES.includes(r.mode)) throw new Error('Невалиден начин на внос в Конто: „' + r.mode + '".');
  if (r.lot_412 && !lotLevels('lot_412', r.lot_412)) throw new Error('Невалидни нива на 412 — всяко ниво веднъж, поне едно.');
  if (r.lot_4532 && !lotLevels('lot_4532', r.lot_4532)) throw new Error('Невалидни нива на 4532 — всяко ниво веднъж, поне едно.');
  for (const k of ['acc_cash', 'acc_customer', 'acc_advance', 'acc_vat']) {
    if (r[k] && !ACCOUNT_RE.test(r[k])) throw new Error('Сметката „' + r[k] + '" трябва да е само от цифри.');
  }
  for (let i = 1; i <= REVENUE_SLOTS; i++) {
    const acc = r['rev' + i + '_account'];
    const lot = r['rev' + i + '_lot'];
    if (acc && !ACCOUNT_RE.test(acc)) throw new Error('Вид приход ' + i + ': сметката „' + acc + '" трябва да е само от цифри.');
    if (lot && lot.split('/').some((x) => !x.trim() || x.trim().length > 20)) throw new Error('Вид приход ' + i + ': партидата се пише с нивата през „/", напр. 001/01.');
  }
}

// Изписването (ПРОМТ-33, services/kontoCogs.js: DEFAULTS): изборите и
// сметките се проверяват при записа — по-добре отказ, отколкото тихо
// „по подразбиране" в файла за Конто.
function checkCogs(r) {
  if (r.export && !['yes', 'no'].includes(r.export)) throw new Error('Невалиден избор за изнасянето на себестойността.');
  if (r.grouping && !['by_document', 'by_day', 'by_month'].includes(r.grouping)) throw new Error('Невалидно групиране на изписванията.');
  for (const k of ['acc_goods', 'acc_material']) if (r[k] && !ACCOUNT_RE.test(r[k])) throw new Error('Сметката „' + r[k] + '" трябва да е само от цифри.');
  for (const k of ['folder', 'doc_type']) if (r[k] && r[k].length > 20) throw new Error('Папката и видът документ са до 20 знака.');
}

function checkPurchaseRecords(r) {
  if (r.advance_mode && !ADVANCE_MODES.includes(r.advance_mode)) throw new Error('Невалиден избор за авансите към доставчици: „' + r.advance_mode + '".');
  if (r.lot_401 && !lotLevels('lot_401', r.lot_401)) throw new Error('Невалидни нива на 401 — всяко ниво веднъж, поне едно.');
  if (r.lot_4531 && !lotLevels('lot_4531', r.lot_4531)) throw new Error('Невалидни нива на 4531 — всяко ниво веднъж, поне едно.');
  for (const k of ['acc_supplier', 'acc_vat', 'acc_expense']) {
    if (r[k] && !ACCOUNT_RE.test(r[k])) throw new Error('Сметката „' + r[k] + '" трябва да е само от цифри.');
  }
}

// Настройките за записванията на ДОСТАВКИТЕ (ПРОМТ-29) — от ефективната
// конфигурация forCompany(company, 'purchases'). Нивата на 401/4531 са
// проверени; невалидно → подразбирането.
function purchaseRecordsOf(cfg) {
  const d = DEFAULTS.purchase_records || {};
  const r = Object.assign({}, d, (cfg && cfg.purchase_records) || {});
  return {
    folder: String(r.folder || '').trim(),
    text: String(r.text || '').trim(),
    acc_supplier: String(r.acc_supplier || d.acc_supplier || '401'), acc_vat: String(r.acc_vat || d.acc_vat || '4531'),
    acc_expense: String(r.acc_expense || d.acc_expense || '602'),
    lot_401: lotLevels('lot_401', r.lot_401) ? String(r.lot_401) : (d.lot_401 || 'partner,no,date'),
    lot_4531: lotLevels('lot_4531', r.lot_4531) ? String(r.lot_4531) : (d.lot_4531 || 'deal,partner,no'),
    deal_full: String(r.deal_full || d.deal_full || '04').trim(),
    deal_no_credit: String(r.deal_no_credit || d.deal_no_credit || '02').trim(),
    deal_partial: String(r.deal_partial || d.deal_partial || '06').trim(),
    deal_no_vat: String(r.deal_no_vat || d.deal_no_vat || '01').trim(),
    activity_code: String(r.activity_code || '').trim(), items_confirmed: String(r.items_confirmed || '').trim(),
    // ПРОМТ-45: „Проект" (ном. 208) по 60x и „МОЛ/Склад" (ном. 201) по 30x —
    // като вида дейност: кодът от Конто, нищо не се създава.
    project_code: String(r.project_code || '').trim(), mol_code: String(r.mol_code || '').trim(),
    default_category: String(r.default_category || '').trim().toLowerCase(),
    advance_mode: ADVANCE_MODES.includes(String(r.advance_mode || '').trim()) ? String(r.advance_mode).trim() : '402',
    measures: Object.assign({}, (cfg && cfg.purchase_measures) || {}),
    items: Object.assign({}, (cfg && cfg.purchase_items) || {}),
  };
}

// Настройките за записванията във вид, удобен за строенето на файла.
// revenues — само попълнените видове приход (със сметка), по реда си; key е
// това, което носи редът на фактурата (rev1…rev6). Без име — „Приход N".
function recordsOf(cfg) {
  const r = Object.assign({}, DEFAULTS.records || {}, (cfg && cfg.records) || {});
  const revenues = [];
  for (let i = 1; i <= REVENUE_SLOTS; i++) {
    const account = String(r['rev' + i + '_account'] || '').trim();
    if (!account) continue;
    const lot = String(r['rev' + i + '_lot'] || '').split('/').map((x) => x.trim()).filter(Boolean);
    revenues.push({ key: 'rev' + i, name: String(r['rev' + i + '_name'] || '').trim() || 'Приход ' + i, account, lot });
  }
  return {
    mode: RECORD_MODES.includes(r.mode) ? r.mode : 'documents',
    folder: String(r.folder || '').trim(),
    acc_cash: String(r.acc_cash || '501'), acc_customer: String(r.acc_customer || '411'),
    acc_advance: String(r.acc_advance || '412'), acc_vat: String(r.acc_vat || '4532'),
    lot_412: lotLevels('lot_412', r.lot_412) ? r.lot_412 : LOT_412[0],
    lot_4532: lotLevels('lot_4532', r.lot_4532) ? r.lot_4532 : LOT_4532[0],
    deal_20: String(r.deal_20 || '01'), deal_9: String(r.deal_9 || '05'),
    text: String(r.text || '').trim(),
    revenues,
  };
}

// Фирмата внася продажбите като записвания (TXT), не като документи.
function isRecords(company) {
  const r = company && company.konto && company.konto.records;
  return !!(r && r.mode === 'records');
}

// Ефективната конфигурация за фирма: копие на файла, върху което ляга
// company.konto. Само НЕпразни стойности презаписват — така полето в
// „Настройки за Конто" може да стои празно и да значи „по подразбиране".
// kind 'purchases': отгоре лягат и отделните настройки на доставките.
function forCompany(company, kind) {
  const over = (company && company.konto && typeof company.konto === 'object') ? company.konto : {};
  const out = Object.assign({}, DEFAULTS);
  const lay = (s, o) => {
    if (!o || typeof o !== 'object') return;
    for (const k of Object.keys(o)) {
      if (!isEmpty(o[k])) out[s][k] = o[k];
    }
  };
  for (const s of SECTIONS) {
    out[s] = Object.assign({}, DEFAULTS[s] || {});
    lay(s, over[s]);
  }
  // Нивата на 412 и 4532 — от данните от Конто, ако фирмата не ги е задала.
  const an = analyticsOf(company);
  if (an) {
    const ownRec = (over.records && typeof over.records === 'object') ? over.records : {};
    for (const [key, acc] of [['lot_412', '412'], ['lot_4532', '4532']]) {
      const lv = an[acc] && an[acc].levels;
      if (isEmpty(ownRec[key]) && Array.isArray(lv)) out.records[key] = lv.length ? lv.join(',') : 'none';
    }
  }
  if (kind === 'purchases') {
    const ownP = (over.purchases && typeof over.purchases === 'object') ? over.purchases : {};
    for (const s of PURCHASE_SECTIONS) if (!['records', 'measures', 'items'].includes(s)) lay(s, ownP[s]);
    // Записванията на доставките: подразбирането, нивата на 401/4531 от
    // данните от Конто (ако фирмата не ги е задала), после своите.
    out.purchase_records = Object.assign({}, DEFAULTS.purchase_records || {});
    const ownRec = (ownP.records && typeof ownP.records === 'object') ? ownP.records : {};
    if (an) {
      for (const [key, acc] of [['lot_401', '401'], ['lot_4531', '4531']]) {
        const lv = an[acc] && an[acc].levels;
        if (isEmpty(ownRec[key]) && Array.isArray(lv)) out.purchase_records[key] = lv.length ? lv.join(',') : 'none';
      }
    }
    // Папката и видовете сделка — от данните на Конто за фирмата.
    const pt = purchasesOf(company);
    if (pt) {
      if (pt.folder) out.purchase_records.folder = pt.folder;
      if (pt.deal) {
        if (pt.deal.full) out.purchase_records.deal_full = pt.deal.full;
        if (pt.deal.none) out.purchase_records.deal_no_credit = pt.deal.none;
        if (pt.deal.partial) out.purchase_records.deal_partial = pt.deal.partial;
      }
      const ownDocs = (over.doc_codes && typeof over.doc_codes === 'object') ? over.doc_codes : {};
      for (const [k, t] of [['purchase_invoice', 'invoice'], ['purchase_credit', 'credit'], ['purchase_debit', 'debit']]) {
        if (isEmpty(ownDocs[k]) && pt.doc && pt.doc[t]) out.doc_codes[k] = pt.doc[t];
      }
    }
    lay('purchase_records', ownRec);
    // Мерките и картата на статиите (ПРОМТ-31) — само по фирма, без подразбиране.
    out.purchase_measures = {};
    lay('purchase_measures', ownP.measures);
    out.purchase_items = {};
    lay('purchase_items', ownP.items);
  }
  // Единичните стойности и кодовете на плащането лягат на горното ниво.
  for (const k of VALUE_KEYS) {
    if (isEmpty(out.values[k])) continue;
    out[k] = k === 'rate' ? toRate(out.values[k], DEFAULTS.rate) : out.values[k];
  }
  if (!isEmpty(out.pay_codes.cash)) out.pay_cash = out.pay_codes.cash;
  if (!isEmpty(out.pay_codes.bank)) out.pay_bank = out.pay_codes.bank;
  if (!VALEUR.includes(out.valeur)) out.valeur = 'eom';
  if (!PARTNER_CODE.includes(out.partner_code)) out.partner_code = 'acc_or_eik';
  return out;
}

// Кодът на документ по тип; празен/липсващ = документът не влиза в Конто.
// cash (т. 3д): платена в брой продажба взема своя код, ако фирмата е задала
// такъв (в Конто той може да е вързан с друга схема — каса 501 вместо 411);
// иначе — същият като по банков път.
function docCode(cfg, docType, cash) {
  const dt = docType || 'invoice';
  if (cash) {
    const c = (cfg.doc_codes_cash || {})[dt];
    if (!isEmpty(c)) return c;
  }
  const v = (cfg.doc_codes || {})[dt];
  return isEmpty(v) ? null : v;
}

// Кодът на вида сделка по данъчен режим; без код → днешният '01'
// (документите отпреди ПРОМТ-7 нямат tax_treatment и трябва да излизат
// точно както досега).
function dealCode(cfg, taxTreatment) {
  const v = (cfg.deal_types || {})[taxTreatment || 'domestic'];
  return isEmpty(v) ? DEFAULTS.deal_types.domestic : v;
}

// Кодът на контрагента във файла (т. 3ж). Без шифър днешният файл праща
// ЕИК-а — а Конто, като не го познае, създава нов контрагент с по-дълъг код.
// „Само шифър" не праща ЕИК изобщо (проверката спира файла преди това).
//
// role 'supplier' (27.10.2026): в Конто клиентите и доставчиците са в различни
// номенклатури (85 и 80) и една фирма често има два различни кода — затова
// контактът носи и „Шифър като доставчик" (supplier_code).
// Без него доставчикът НЕ взема счетоводния шифър (Макс, 29.10.2026): кодът от
// 85 в номенклатура 80 може да е на съвсем друга фирма, а Конто търси първо по
// код и би осчетоводил доставката тихо на нея. Без „Шифър като доставчик"
// партидата е ЕИК-ът — Конто намира доставчика по него или го създава.
function codeOf(c, role) {
  const x = c || {};
  if (role === 'supplier') return String(x.supplier_code || '').trim();
  return String(x.acc_code || '').trim();
}
function partnerCode(cfg, c, role) {
  const x = c || {};
  const code = codeOf(x, role);
  if (cfg && cfg.partner_code === 'acc_only') return code;
  return code || x.eik || '';
}

module.exports = { ADVANCE_MODES, PURCHASES, purchasesOf, ANALYTICS, analyticsOf, PURCHASE_SECTIONS, PURCHASE_RECORD_KEYS, purchaseRecordsOf, CC_FIELD_RE, DEFAULTS, RECORD_PRESETS, SECTIONS, VALUE_KEYS, VALEUR, PARTNER_CODE, FIELD_KEYS, LIMITS, RECORD_MODES, LOT_412, LOT_4532, LOT_PARTS, lotLevels, REVENUE_SLOTS, RECORD_KEYS, forCompany, docCode, dealCode, partnerCode, codeOf, sanitize, isEmpty, recordsOf, isRecords };
